<template>
    <flex-element :element-type="elementType" :tag="renderTag" class="f-flex f-flex-col f-relative f-z-1">
        <droppable
            v-if="editable && !publicMode && !isLayoutComponent"
            force-display
            :index="0"
            :is-structural-component="isLayoutComponent"
            element-type="droppable"
            scroll-on-drop
            @drop="data => onDrop(data, 0)"
            @click.native.right="event => openPasteMenu(event, 0)"
            @paste="pasteElement"
        />

        <transition-group
            v-if="elementsNew.length"
            name="app-components"
            tag="div"
            class="f-w-1/1 f-h-1/1"
        >
            <div v-for="(element, elementIndex) in elementsNew" :key="element.id" class="fc-display-contents">
                <component
                    :is="element.elementType"
                    :ref="`${ element.elementType }_${ elementIndex }`"
                    v-bind="element"
                    :index="elementIndex"
                    :pointer="elementIndex + 1"
                    :top-level="topLevel"
                    data-organism
                    is-dynamic-element
                    is-dropzone-element
                    @click.native.right="event => openCopyMenu(event, element)"
                    @element-updated="elementUpdated"
                    @cut="onCut({ element, onCutCallback: deleteElement })"
                    @copy="onCopy"
                    @delete-element="deleteElementByIndex"
                    @move-element="moveElement"
                />

                <droppable
                    v-if="editable && !publicMode && !isLayoutComponent"
                    type="organism"
                    force-display
                    :index="elementIndex + 1"
                    :zone="'column_' + elementIndex + 1"
                    :is-structural-component="isLayoutComponent"
                    element-type="droppable"
                    scroll-on-drop
                    @drop="data => onDrop(data, elementIndex + 1)"
                    @click.native.right="event => openPasteMenu(event, elementIndex + 1)"
                    @paste="pasteElement"
                />
            </div>
        </transition-group>

        <droppable
            v-if="editable && !publicMode && isLayoutComponent"
            type="organism"
            force-display
            :index="elementsNew.length"
            scroll-on-drop
            :zone="'column_' + elementsNew.length"
            element-type="droppable"
            @drop="data => onDrop(data, elementsNew.length)"
            @click.native.right="event => openPasteMenu(event, elementsNew.length)"
            @paste="pasteElement"
        />
    </flex-element>
</template>

<script>
import { mapMutations, mapState } from 'vuex';

import flexDeepCopy from '@/utils/flex-deep-copy';
import baseOrganism from '@/mixins/base-organism/base-organism-smart';
import cutCopyPasteMixin from '@/mixins/cut-copy-paste';
import { CLEAR_SELECTED_ELEMENTS } from '@/store/types/mutation-types';
import { classDictionary } from '@/data/tailwind';
import { generateRendering } from '@/utils/rendering';

export default {
    name: 'drop-zone-multi',
    mixins: [baseOrganism, cutCopyPasteMixin],

    props: {
        displayName: {
            type: String,
            default: 'Drop Zone',
        },

        columnClasses: {
            type: String,
            default: '',
        },

        columnIndex: {
            type: Number,
            default: 0,
        },

        editable: {
            type: Boolean,
            default: true,
        },

        index: {
            type: Number,
            default: 0,
        },

        pageId: {
            type: String,
            default: '',
        },

        topLevel: {
            type: Boolean,
            default: false,
        },

        isLayoutComponent: {
            type: Boolean,
            default: false,
        },

    },

    data() {
        return {
            elementsOriginalLength: 0,
            publicMode: process.env.VUE_APP_ENV === 'public',
            valueNew: '',
        };
    },

    computed: {
        ...mapState({
            copiedElements: state => state.page.copiedElements,
            onCutCallback: state => state.page.onCutCallback,
            moving: state => state.page.moving,
            selectedElements: state => state.page.selectedElements,
        }),

        renderTag() {
            return this.tagNew || this.tagDefault;
        },
    },

    watch: {
        columnClasses(newClasses) {
            this.setColumnClasses(newClasses);
        },

        htmlClass(newHtmlClass) {
            this.$emit('class-updated', newHtmlClass, this.columnIndex);
        },
    },

    beforeMount() {
        this.setFieldValues();
    },

    created() {
        this.atomicType = 'organism';
    },

    mounted() {
        if (this.htmlClass.length === 0) {
            this.setColumnClasses(this.columnClasses);
        } else {
            this.$emit('class-updated', this.htmlClass, this.columnIndex);
        }
    },

    methods: {
        ...mapMutations({
            clearSelectedElements: CLEAR_SELECTED_ELEMENTS,
        }),

        addRendering(rendering, index) {
            let insertIndex = index;

            if (insertIndex > this.elementsNew.length) {
                insertIndex = this.elementsNew.length;
            }

            this.elementsNew.splice(insertIndex, 0, rendering);

            for (let i = insertIndex + 1; i < this.elementsNew.length; i++) {
                this.elementsNew[i].pointer++;
            }

            this.setChangedData(true);
        },

        async onDrop(data, index) {
            const rendering = await generateRendering(data, index);

            this.addRendering(rendering, index);
        },

        openCopyMenu(event, element) {
            if (process.env.VUE_APP_ENV !== 'public') {
                event.preventDefault();
                event.stopPropagation();
            }

            if (!this.selectedElements.length) return;

            this.$root.$emit('flex::context::open', {
                event,
                id: 'cut-copy-context-menu',
                data: {
                    element,
                    onCutCallback: this.deleteElement,
                },
            });
        },

        openPasteMenu(event, index) {
            if (process.env.VUE_APP_ENV !== 'public') {
                event.preventDefault();
                event.stopPropagation();
            }
            this.$root.$emit('flex::context::open', {
                event,
                id: 'paste-context-menu',
                data: {
                    index,
                    onPasteCallback: this.pasteElement,
                },
            });
        },

        onClick(e) {
            // overriding to prevent setselectedelement for the page-level drop zone
            if (this.isLayoutComponent) this.onSetElementSelection(e);

            // flex-element swallows the clicks, thus preventing context menu from closing:
            this.$root.$emit('flex::context::close');

            // cancel movement mode if a new element is selected
            if (this.moving) {
                this.clearCopiedRendering();
            }
        },

        async pasteElement(index) {
            const { elements, mode } = this.copiedElements;
            let targetIndex = index;
            let firstPastedElement = null;

            if (mode === 'cut') this.onCutCallback();

            elements.forEach((elem, i) => {
                if (mode === 'cut') {
                    const copyIndex = this.elementsNew.findIndex(r => r.id === elem.id);

                    // cutting and pasting into self should do nothing
                    const isPastingIntoSelf = copyIndex === targetIndex || copyIndex === targetIndex - 1;
                    const isLayoutRendering = copyIndex === -1;

                    if (isPastingIntoSelf && !isLayoutRendering) return;
                }

                const objClone = mode === 'copy' ? flexDeepCopy(elem) : { ...elem };

                this.addRendering(objClone, targetIndex);
                targetIndex++;

                if (i === 0) firstPastedElement = objClone;
            });

            this.afterPaste(firstPastedElement);
        },

        deleteElement(element) {
            const elementIndex = this.elementsNew.findIndex(elementNew => elementNew.id === element.id);

            this.deleteElementByIndex(elementIndex);
        },

        deleteElementByIndex(index) {
            if (index > -1) this.elementsNew.splice(index, 1);

            this.clearSelectedElements();

            this.setChangedData(true);
        },

        async moveElement(direction, index) {
            let to = index;

            if (direction === 'up') {
                to = index - 1;
            } else {
                to = index + 1;
            }

            if (to < 0 || to === this.elementsNew.length) return;

            const element = this.elementsNew[index];

            this.elementsNew.splice(index, 1);
            this.elementsNew.splice(to, 0, element);
            this.setChangedData(true);

            await this.$nextTick();

            this.$root.$emit('flex::component::move');
        },

        setColumnClasses(classString) {
            if (!classString) return;

            classString.split(' ').forEach((className) => {
                const classDefinition = classDictionary[className];

                if (!classDefinition) return;

                const tailwindDefinitionProp = classDefinition.tailwindDefinitionProp;
                const screen = classDefinition.screen;

                this.flexClassCollection[tailwindDefinitionProp][screen].default = className;
            });
        },

        setFieldValues() {
            this.tagNew = this.tag;

            if (this.elements) {
                this.elementsOriginalLength = this.elements.length;
            }
        },
    },
};
</script>
